import {
  useMyAccess,
  useRefreshAccessOnDenial,
} from "@/features/internal-user/hooks/use-access";
import { AreaAccess, CrmArea } from "@/features/internal-user/services/access";
import { PermissionType } from "@/helpers/role-permission";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { toast } from "@/hooks/use-toast";
import AccessUnavailable, {
  AccessFailure,
  classifyAccessError,
} from "./access-unavailable";

type PermissionContextType = {
  permissions: PermissionType[];
  areas: Record<CrmArea, AreaAccess>;
  isSuper: boolean;
  /** The role name, for display only — access decisions use the fields above. */
  role?: string;
  isLoaded?: boolean;
};

const PermissionContext = createContext<PermissionContextType>({
  permissions: [],
  areas: {},
  isSuper: false,
  isLoaded: false,
});

/**
 * Access comes from the server, and from nowhere else.
 *
 * Roles, verbs and area grants are rows in our database (RH-891/RH-892), so
 * `/internal/access/me` is the only thing that knows what this person may do.
 * There used to be a fallback that read the caller's Clerk `publicMetadata`
 * when that call failed. It is gone, and it had to go: nothing writes those
 * fields any more, so they are empty for anyone new and stale for everyone
 * else, and a hand-edited `role: super_admin` in the Clerk dashboard would have
 * opened every area of the UI on nothing but the browser's say-so.
 *
 * So a failure here renders an explanation instead of the CRM. That is stricter
 * than before, and more honest: the old fallback produced a CRM that said "your
 * role has not been given View access to any area", which blamed the access
 * grid for what was usually a request that never arrived.
 *
 * With one distinction, made below: an ANSWER replaces the CRM, a failure to
 * answer does not. A 401 (dead token), a 403 (role grants nothing, or no staff
 * record) are answers, and they replace the CRM even mid-session. A 503 or a
 * request that never arrived is not an answer; if the server already told us
 * this person's access once, that answer stays in force and the CRM stays up.
 * Without this, the refetch that follows every Manage Access toggle could turn
 * one transient failure into a full-screen error for an operator who was fine
 * a second ago.
 *
 * That split carries more weight than it used to. /me is no longer asked once
 * per page load: it is re-asked when the tab regains focus, and whenever any
 * request comes back 401 or 403 (useRefreshAccessOnDenial). So mid-session
 * answers — including the ones that end the session — are now the normal case
 * rather than something only a reload produced.
 */
const FALLS_BACK_TO_CACHED: readonly AccessFailure[] = ["unavailable"];

export const PermissionProvider = ({ children }: { children: ReactNode }) => {
  const { data, isLoading, isError, error, refetch, isFetching } =
    useMyAccess();

  // A refused request anywhere in the CRM re-asks /me. Installed here because
  // this is the component that renders the answer; it runs before the early
  // return below, so it is in place even while the explanation screen is up.
  useRefreshAccessOnDenial();

  const failure = isError ? classifyAccessError(error) : null;
  const keepCached =
    failure !== null && !!data && FALLS_BACK_TO_CACHED.includes(failure);

  // Say so once per failed refresh, quietly: the person can keep working, and
  // React Query will retry on the next invalidation.
  const lastReported = useRef<unknown>(null);
  useEffect(() => {
    if (!keepCached || lastReported.current === error) return;
    lastReported.current = error;
    toast({
      title: "Couldn't refresh your access",
      description:
        "Showing what you could do a moment ago. It will be re-checked automatically.",
    });
  }, [keepCached, error]);

  // Memoised because this value reaches ~80 files and every one of the 113
  // <HasAccess> gates. Each refocus refetch flips isFetching twice, and React
  // Query hands back the same `data` object when nothing changed — so without
  // this the identical answer would still re-render the whole tree, twice, on
  // every tab switch.
  const value = useMemo(
    () => ({
      permissions: data?.permissions ?? [],
      areas: data?.areas ?? {},
      isSuper: data?.is_super ?? false,
      role: data?.role,
      // Fails closed while loading, as it did before.
      isLoaded: !isLoading && !!data,
    }),
    [data, isLoading],
  );

  if (failure && !keepCached) {
    return (
      <AccessUnavailable
        failure={failure}
        onRetry={() => void refetch()}
        isRetrying={isFetching}
      />
    );
  }

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissionContext = () => useContext(PermissionContext);
